import { __extends } from "tslib";
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * SpecularBasicMethod provides the default shading method for Blinn-Phong
 * specular highlights (an optimized but approximated version of Phong
 * specularity).
 */
var SpecularBasicMethod = /** @class */ (function (_super) {
    __extends(SpecularBasicMethod, _super);
    /**
     * Creates a new SpecularBasicMethod object.
     */
    function SpecularBasicMethod() {
        var _this = _super.call(this) || this;
        _this._gloss = 50;
        _this._strength = 1;
        _this._color = 0xffffff;
        return _this;
    }
    Object.defineProperty(SpecularBasicMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return SpecularBasicMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularBasicMethod.prototype, "gloss", {
        /**
         * The glossiness of the material (sharpness of the specular highlight).
         */
        get: function () {
            return this._gloss;
        },
        set: function (value) {
            if (this._gloss == value)
                return;
            this._gloss = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularBasicMethod.prototype, "strength", {
        /**
         * The overall strength of the specular highlights.
         */
        get: function () {
            return this._strength;
        },
        set: function (value) {
            if (this._strength == value)
                return;
            this._strength = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularBasicMethod.prototype, "color", {
        /**
         * The colour of the specular reflection of the surface.
         */
        get: function () {
            return this._color;
        },
        set: function (value) {
            if (this._color == value)
                return;
            this._color = value;
            // specular is now either enabled or disabled
            if (this._color == 0 || value == 0)
                this.invalidateShaderProgram();
            else
                this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularBasicMethod.prototype, "texture", {
        /**
         * A texture that defines the strength of specular reflections for each
         * texel in the red channel, and the gloss factor (sharpness) in the green
         * channel. You can use Specular2DTexture if you want to easily set specular
         * and gloss maps from grayscale images, but correctly authored images are
         * preferred.
         */
        get: function () {
            return this._texture;
        },
        set: function (value) {
            if (this._texture == value)
                return;
            if (this._texture)
                this.iRemoveTexture(this._texture);
            this._texture = value;
            if (this._texture)
                this.iAddTexture(this._texture);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    SpecularBasicMethod.prototype.dispose = function () {
        this._texture = null;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicMethod.prototype.copyFrom = function (method) {
        var spec = method;
        this.texture = spec.texture;
        this.strength = spec.strength;
        this.color = spec.color;
        this.gloss = spec.gloss;
    };
    SpecularBasicMethod.assetType = '[asset SpecularBasicMethod]';
    return SpecularBasicMethod;
}(MethodBase));
export { SpecularBasicMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_SpecularBasicMethod provides the default shading method for
 * Blinn-Phong specular highlights (an optimized but approximated version of
 * Phong specularity).
 */
var _Shader_SpecularBasicMethod = /** @class */ (function (_super) {
    __extends(_Shader_SpecularBasicMethod, _super);
    /**
     * Creates a new EffectEnvMapChunk.
     */
    function _Shader_SpecularBasicMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    _Shader_SpecularBasicMethod.prototype._isUsed = function () {
        if (!this._shader.numLights)
            return false;
        return true;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._initVO = function (chunkVO) {
        chunkVO.needsNormals = this._shader.numLights > 0;
        chunkVO.needsView = this._shader.numLights > 0;
        if (this._method.texture) {
            this._texture = this._method.texture.getAbstraction(this._shader);
            this._texture._initVO(chunkVO);
            this._shader.uvDependencies++;
        }
        else if (this._texture) {
            this._texture = null;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._initConstants = function () {
        if (this._method.texture)
            this._texture._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._totalLightColorReg = null;
        this._specularTexData = null;
        this._specularDataRegister = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        var code = '';
        this._pIsFirstLight = true;
        this._specularDataRegister = registerCache.getFreeFragmentConstant();
        this._specularDataIndex = this._specularDataRegister.index * 4;
        if (this._texture) {
            this._specularTexData = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(this._specularTexData, 1);
            code += this._texture._getFragmentCode(this._specularTexData, registerCache, sharedRegisters, sharedRegisters.uvVarying);
        }
        this._totalLightColorReg = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(this._totalLightColorReg, 1);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._getFragmentCodePerLight = function (lightDirReg, lightColReg, registerCache, sharedRegisters) {
        var code = '';
        var t;
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        var viewDirReg = sharedRegisters.viewDirFragment;
        var normalReg = sharedRegisters.normalFragment;
        // blinn-phong half vector model
        code += 'add ' + t + ', ' + lightDirReg + ', ' + viewDirReg + '\n' +
            'nrm ' + t + '.xyz, ' + t + '\n' +
            'dp3 ' + t + '.w, ' + normalReg + ', ' + t + '\n' +
            'sat ' + t + '.w, ' + t + '.w\n';
        if (this._texture) {
            // apply gloss modulation from texture
            code += 'mul ' + this._specularTexData + '.w, ' + this._specularTexData + '.y, ' + this._specularDataRegister + '.w\n' +
                'pow ' + t + '.w, ' + t + '.w, ' + this._specularTexData + '.w\n';
        }
        else {
            code += 'pow ' + t + '.w, ' + t + '.w, ' + this._specularDataRegister + '.w\n';
        }
        // attenuate
        if (this._shader.usesLightFallOff)
            code += 'mul ' + t + '.w, ' + t + '.w, ' + lightDirReg + '.w\n';
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        code += 'mul ' + t + '.xyz, ' + lightColReg + ', ' + t + '.w\n';
        if (!this._pIsFirstLight) {
            code += 'add ' + this._totalLightColorReg + '.xyz, ' + this._totalLightColorReg + ', ' + t + '\n';
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._getFragmentCodePerProbe = function (cubeMapReg, weightRegister, registerCache, sharedRegisters) {
        var code = '';
        var t;
        // write in temporary if not first light, so we can add to total diffuse colour
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        var normalReg = sharedRegisters.normalFragment;
        var viewDirReg = sharedRegisters.viewDirFragment;
        code += 'dp3 ' + t + '.w, ' + normalReg + ', ' + viewDirReg + '\n' +
            'add ' + t + '.w, ' + t + '.w, ' + t + '.w\n' +
            'mul ' + t + ', ' + t + '.w, ' + normalReg + '\n' +
            'sub ' + t + ', ' + t + ', ' + viewDirReg + '\n' +
            'tex ' + t + ', ' + t + ', ' + cubeMapReg + ' <cube,' + 'linear' + ',miplinear>\n' +
            'mul ' + t + '.xyz, ' + t + ', ' + weightRegister + '\n';
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        if (!this._pIsFirstLight) {
            code += 'add ' + this._totalLightColorReg + '.xyz, ' + this._totalLightColorReg + ', ' + t + '\n';
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = '';
        if (sharedRegisters.shadowTarget)
            code += 'mul ' + this._totalLightColorReg + '.xyz, ' + this._totalLightColorReg + ', ' + sharedRegisters.shadowTarget + '.w\n';
        if (this._texture) {
            // apply strength modulation from texture
            code += 'mul ' + this._totalLightColorReg + '.xyz, ' + this._totalLightColorReg + ', ' + this._specularTexData + '.x\n';
            registerCache.removeFragmentTempUsage(this._specularTexData);
        }
        // apply material's specular reflection
        code += 'mul ' + this._totalLightColorReg + '.xyz, ' + this._totalLightColorReg + ', ' + this._specularDataRegister + '\n' +
            'add ' + targetReg + '.xyz, ' + targetReg + ', ' + this._totalLightColorReg + '\n';
        registerCache.removeFragmentTempUsage(this._totalLightColorReg);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularBasicMethod.prototype._activate = function () {
        if (this._texture)
            this._texture.activate();
        if (this._invalid) {
            var index = this._specularDataIndex;
            var data = this._shader.fragmentConstantData;
            data[index] = ((this._method.color >> 16) & 0xff) / 0xff * this._method.strength;
            data[index + 1] = ((this._method.color >> 8) & 0xff) / 0xff * this._method.strength;
            data[index + 2] = (this._method.color & 0xff) / 0xff * this._method.strength;
            data[index + 3] = this._method.gloss;
        }
    };
    _Shader_SpecularBasicMethod.prototype._setRenderState = function (renderState) {
        if (this._texture)
            this._texture._setRenderState(renderState);
    };
    return _Shader_SpecularBasicMethod;
}(_Shader_MethodBase));
export { _Shader_SpecularBasicMethod };
ShaderBase.registerAbstraction(_Shader_SpecularBasicMethod, SpecularBasicMethod);
